import React from 'react';
import {
  Box,
  Flex,
  TextField,
  Table,
  TablePagination,
  ColumnConfig,
  CellText,
  ButtonLink,
  HeaderPage,
} from '@backstage/ui';
import { RiSearchLine } from '@remixicon/react';
import { Content } from '@backstage/core-components';
import { attachComponentData } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogPlugin } from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  EntityTextFilter,
  useEntityList,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { Entity, parseEntityRef } from '@backstage/catalog-model';
import { appTranslationRef } from '../../../core/i18n';
import styles from './clients.page.module.css';

/**
 * Kind filter locked to Domain that does NOT expose toQueryValue(),
 * so EntityListProvider won't serialize it to the URL.
 */
class DomainKindFilter {
  readonly value = 'Domain';
  getCatalogFilters() {
    return { kind: this.value };
  }
}

// ============================================================================
// Filter Components
// ============================================================================

/** Always forces kind=Domain without touching the URL */
const DomainKindSync = () => {
  const { updateFilters } = useEntityList();

  React.useEffect(() => {
    updateFilters({ kind: new DomainKindFilter() as any });
  }, [updateFilters]);

  return null;
};

const SearchFilter = () => {
  const { queryParameters, updateFilters } = useEntityList();
  const { t } = useTranslationRef(appTranslationRef);

  const initialSearch = React.useMemo(
    () => [queryParameters.text].flat().filter(Boolean)[0] as string | undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [], // only on mount
  );
  const [searchText, setSearchText] = React.useState(initialSearch ?? '');

  // Debounce search → EntityListProvider (URL sync handled by provider)
  React.useEffect(() => {
    const timer = setTimeout(() => {
      updateFilters({
        text: searchText ? new EntityTextFilter(searchText) : undefined,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, updateFilters]);

  return (
    <TextField
      className={styles.searchInput}
      placeholder={t('clients.filters.searchPlaceholder')}
      value={searchText}
      onChange={setSearchText}
      icon={<RiSearchLine size={18} />}
    />
  );
};

// ============================================================================
// Table Content
// ============================================================================

interface ClientTableItem {
  id: string;
  entity: Entity;
  name: string;
  owner: string;
  description: string;
}

const ClientTableContent = () => {
  const { entities, loading, totalItems, limit, setLimit, offset, setOffset } =
    useEntityList();
  const { t } = useTranslationRef(appTranslationRef);

  const tableData: ClientTableItem[] = React.useMemo(() => {
    return entities.map(entity => {
      const rawOwner = entity.spec?.owner as string | undefined;
      const ownerName = rawOwner
        ? parseEntityRef(rawOwner, { defaultKind: 'group', defaultNamespace: 'default' }).name
        : '-';
      return {
        id: `${entity.metadata.namespace || 'default'}-${entity.kind}-${entity.metadata.name}`,
        entity,
        name: entity.metadata.title || entity.metadata.name,
        owner: ownerName,
        description: entity.metadata.description || '-',
      };
    });
  }, [entities]);

  const columnConfig: ColumnConfig<ClientTableItem>[] = [
    {
      id: 'name',
      label: t('clients.table.name'),
      isRowHeader: true,
      cell: item => <CellText title={item.name} />,
    },
    {
      id: 'owner',
      label: t('clients.table.owner'),
      cell: item => <CellText title={item.owner} />,
    },
    {
      id: 'description',
      label: t('clients.table.description'),
      cell: item => {
        const desc = item.description;
        const truncated =
          desc.length > 60 ? `${desc.substring(0, 60)}...` : desc;
        return <CellText title={truncated} />;
      },
    },
  ];

  const rowConfig = {
    getHref: (item: ClientTableItem) => {
      const params = entityRouteParams(item.entity);
      return `/catalog/${params.namespace}/${params.kind}/${params.name}`;
    },
  };

  const currentOffset = offset ?? 0;
  const total = totalItems ?? 0;
  const hasNextPage = currentOffset + limit < total;
  const hasPreviousPage = currentOffset > 0;

  const pageSizeOptions = React.useMemo(
    () => [
      {
        label: `${t('clients.pagination.show')} 5 ${t('clients.pagination.results')}`,
        value: 5,
      },
      {
        label: `${t('clients.pagination.show')} 10 ${t('clients.pagination.results')}`,
        value: 10,
      },
      {
        label: `${t('clients.pagination.show')} 20 ${t('clients.pagination.results')}`,
        value: 20,
      },
      {
        label: `${t('clients.pagination.show')} 50 ${t('clients.pagination.results')}`,
        value: 50,
      },
    ],
    [t],
  );

  const getPaginationLabel = React.useCallback(
    ({
      offset: labelOffset,
      totalCount,
    }: {
      pageSize: number;
      offset?: number;
      totalCount?: number;
    }) => {
      if (labelOffset !== undefined && totalCount !== undefined) {
        const from = labelOffset + 1;
        const to = Math.min(labelOffset + limit, totalCount);
        return `${from} - ${to} ${t('clients.pagination.of')} ${totalCount}`;
      }
      return `${totalCount ?? 0} ${t('clients.pagination.items')}`;
    },
    [t, limit],
  );

  return (
    <Box className={styles.flexColumnScroll}>
      <Box className={styles.tableContainer}>
        <Table
          columnConfig={columnConfig}
          data={tableData}
          rowConfig={rowConfig}
          loading={loading}
          pagination={{ type: 'none' }}
          className={styles.table}
        />
        <TablePagination
          pageSize={limit}
          totalCount={totalItems}
          hasNextPage={hasNextPage}
          hasPreviousPage={hasPreviousPage}
          onNextPage={() => setOffset?.(currentOffset + limit)}
          onPreviousPage={() => setOffset?.(Math.max(0, currentOffset - limit))}
          onPageSizeChange={(newLimit) => {
            setLimit(newLimit);
            setOffset?.(0);
          }}
          pageSizeOptions={pageSizeOptions}
          getLabel={getPaginationLabel}
          showPageSizeOptions
        />
      </Box>
    </Box>
  );
};

// ============================================================================
// Page Component
// ============================================================================

const ClientsPageComponent = () => {
  const { t } = useTranslationRef(appTranslationRef);

  return (
    <Box className={styles.pageShell}>
      <HeaderPage
        title={t('clients.pageTitle')}
        customActions={
          <ButtonLink href="/create" variant="primary">
            {t('clients.createButton')}
          </ButtonLink>
        }
      />
      <Content stretch>
        <EntityListProvider pagination={{ mode: 'offset', limit: 20 }}>
          <DomainKindSync />
          <Flex direction="column" gap="4">
            {/* Filters toolbar */}
            <Flex gap="3" align="end" className={styles.filtersToolbar}>
              <Box className={styles.searchWrapper}>
                <SearchFilter />
              </Box>
            </Flex>

            {/* Table content */}
            <ClientTableContent />
          </Flex>
        </EntityListProvider>
      </Content>
    </Box>
  );
};

// Attach the catalogIndex routeRef so EntityLayout can resolve the "back to catalog" link
attachComponentData(
  ClientsPageComponent,
  'core.mountPoint',
  catalogPlugin.routes.catalogIndex,
);

export const ClientsPage = ClientsPageComponent;
